/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * T-3: the open/close, focus and keyboard behaviour of the site's language
 * control.
 *
 * P-4 (Session 376) updated this note, and the update matters — read it before
 * reintroducing anything. This used to say the header switcher and the sign-in
 * pages' switcher were kept as separate components because the header one "is
 * styled for a dark navy bar with a globe icon and a boxed trigger, which on a
 * light sign-in page reads as a stray piece of the site header". That reasoning
 * was correct about where the auth switcher sat: on the light page body, below
 * the card. It does not apply to the auth header band, which is #01183d — a
 * dark navy bar, the one place on those pages where the shared control is
 * already correctly drawn. So the control moved there, the duplicate component
 * was deleted, and there is now one switcher rather than two. Do not restore
 * the second one on the strength of the sentence this replaced.
 *
 * Everything a listbox needs is here:
 *   - Escape closes the list and returns focus to the trigger
 *   - Down or Up on the trigger opens the list and moves focus into it (F-7,
 *     Session 393 — the arrow keys existed and were correct, but nothing ever
 *     moved focus off the trigger, so the first thing a keyboard user tries
 *     did nothing at all)
 *   - Arrow keys move between options without wrapping past either end
 *   - Enter and Space open the trigger and choose an option
 *   - a click anywhere outside closes the list
 *   - focus returns to the trigger after a choice, so the customer is not
 *     dropped back at the top of the page
 */
export interface LanguageDropdown {
  open: boolean;
  setOpen: (open: boolean) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  /** Choose a language, close the list and return focus to the trigger. */
  select: (langId: string) => void;
  /** Enter / Space to toggle, Down / Up to open into the list, Escape to
   *  close. Attach to the trigger. */
  onTriggerKeyDown: (event: React.KeyboardEvent) => void;
  /** Escape and arrow keys. Attach to the listbox. */
  onListKeyDown: (event: React.KeyboardEvent) => void;
}

export function useLanguageDropdown(
  onLanguageChange: (lang: string) => void,
): LanguageDropdown {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /* F-7 (Session 393): which end of the list the next open should land on.
     Set by the trigger's arrow keys, consumed by the effect below once the
     list is actually on the page. */
  const pendingFocus = useRef<"first" | "last" | null>(null);

  /* F-7: move focus into the list after it opens.
   *
   * The arrow-key handler is attached to the list, which is a SIBLING of the
   * trigger rather than an ancestor of it, so a key pressed while focus is
   * still on the trigger never reaches it. The customer tabbed to the trigger,
   * pressed Enter, and then pressed Down — and nothing happened at all, on a
   * control that announces itself with aria-haspopup="listbox" and
   * role="listbox" and so tells a screen-reader user to expect exactly this.
   * Only Tab, which moves focus into the first option, made the arrow keys
   * start working.
   *
   * An effect rather than a timer: it runs after the list has been added to
   * the page, so the options exist to focus, and it stays deterministic in
   * tests. */
  useEffect(() => {
    if (!open || !pendingFocus.current) return;
    const options =
      containerRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="option"]',
      );
    if (options?.length) {
      const selected = Array.from(options).findIndex(
        (el) => el.getAttribute("aria-selected") === "true",
      );
      const index =
        pendingFocus.current === "last"
          ? options.length - 1
          : selected >= 0
            ? selected
            : 0;
      options[index]?.focus();
    }
    pendingFocus.current = null;
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const select = useCallback(
    (langId: string) => {
      setOpen(false);
      onLanguageChange(langId);
      triggerRef.current?.focus();
    },
    [onLanguageChange],
  );

  const onTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen((prev) => !prev);
      } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        /* F-7: the listbox pattern this control announces. Down opens the list
           at the current language, Up opens it at the last option. */
        e.preventDefault();
        pendingFocus.current = e.key === "ArrowDown" ? "first" : "last";
        setOpen(true);
      } else if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    },
    [open],
  );

  const onListKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;

    e.preventDefault();
    const options =
      containerRef.current?.querySelectorAll<HTMLButtonElement>(
        '[role="option"]',
      );
    if (!options?.length) return;

    const currentIndex = Array.from(options).findIndex(
      (el) => el === document.activeElement,
    );
    const nextIndex =
      e.key === "ArrowDown"
        ? Math.min(currentIndex + 1, options.length - 1)
        : Math.max(currentIndex - 1, 0);
    options[nextIndex]?.focus();
  }, []);

  return {
    open,
    setOpen,
    containerRef,
    triggerRef,
    select,
    onTriggerKeyDown,
    onListKeyDown,
  };
}
